import { templateImageAPI, imageStorageAPI, getFundingToken } from '@/utils/api';
import { getImageFromIndexedDB } from '@/utils/imageStorage';
import { rebaseUploadsUrl, resolveFetchableImageUrl, fetchImageUrlAsDataUrl } from '@/utils/imageUrlUtils';
import { resolveImageSpaceDisplayRawUrl } from './imageSpaceDisplayUrl';

function pickUrlFromImageStorageResponse(result) {
  if (!result || typeof result !== 'object') return '';
  const candidates = [result.url, result.data?.url, result.data?.path, result.data?.fileUrl];
  for (const c of candidates) {
    if (!c || typeof c !== 'string') continue;
    const s = String(c).trim();
    if (s.startsWith('/uploads/') || s.startsWith('data:image/')) return s;
    if (s.startsWith('http')) {
      try {
        const p = new URL(s).pathname;
        if (p.startsWith('/uploads/')) return p;
      } catch {
        /* ignore */
      }
    }
  }
  return '';
}

/**
 * 转为 canvas 可绘制的 data URL。失败时返回空串（绝不回退到 http，避免贴图 onerror）。
 * @param {string} rawUrl
 * @param {{ imageId?: string, loadImageSpaceRecord?: (id: string) => Promise<object|null> }} [options]
 */
export async function resolveDrawableImageUrl(rawUrl, options = {}) {
  const { imageId, loadImageSpaceRecord } = options;

  if (imageId && typeof loadImageSpaceRecord === 'function') {
    try {
      const rec = await loadImageSpaceRecord(String(imageId));
      const embedded = rec?.dataUrl || rec?.data?.dataUrl;
      if (embedded && String(embedded).startsWith('data:image/')) {
        return String(embedded);
      }
      const fromRec = await resolveUrlFromImageSpaceRecord(rec);
      if (fromRec && fromRec !== rawUrl) {
        const nested = await resolveDrawableImageUrl(fromRec);
        if (nested.startsWith('data:image/')) return nested;
      }
    } catch {
      /* continue */
    }
  }

  if (imageId) {
    try {
      const local = await getImageFromIndexedDB(String(imageId));
      if (local && String(local).startsWith('data:image/')) return String(local);
      if (local) {
        const fromLocal = await resolveDrawableImageUrl(local);
        if (fromLocal.startsWith('data:image/')) return fromLocal;
      }
    } catch {
      /* continue */
    }
  }

  const rebased = rebaseUploadsUrl(rawUrl);
  if (!rebased) return '';
  if (rebased.startsWith('data:image/')) return rebased;
  if (rebased.startsWith('blob:')) {
    const blobData = await fetchImageUrlAsDataUrl(rebased, { getAuthToken: getFundingToken });
    if (blobData.startsWith('data:image/')) return blobData;
  }

  let dataUrl = await fetchImageUrlAsDataUrl(rebased, { getAuthToken: getFundingToken });
  if (dataUrl.startsWith('data:image/')) return dataUrl;

  if (rebased.startsWith('http://') || rebased.startsWith('https://') || rebased.includes('/uploads/')) {
    try {
      const saved = await imageStorageAPI.save(rebased, { folder: 'image-space', registerImageSpace: false });
      const path = pickUrlFromImageStorageResponse(saved);
      if (path) {
        dataUrl = await fetchImageUrlAsDataUrl(
          path.startsWith('/') ? path : rebaseUploadsUrl(path),
          { getAuthToken: getFundingToken },
        );
        if (dataUrl.startsWith('data:image/')) return dataUrl;
      }
    } catch (e) {
      console.warn('[主图创建] signed-image 转存失败', e);
    }
  }

  return '';
}

function pickRecordUrl(rec) {
  if (!rec || typeof rec !== 'object') return '';
  return (rec.dataUrl || rec.url || rec.imageUrl || '').trim();
}

/**
 * 加载套图模版各轮播图的可用 URL（服务端 templateImage + 本地 IndexedDB + 模版 preview）。
 */
export async function buildTemplateSlideUrlMap(templateId, templateImages, templateData) {
  const map = {};
  let store = {};
  try {
    const res = await templateImageAPI.getByTemplate(templateId);
    store = res?.images && typeof res.images === 'object' ? res.images : {};
  } catch {
    store = {};
  }

  const slides = Array.isArray(templateImages) ? templateImages : [];
  const previewDataUrl =
    typeof templateData?.previewDataUrl === 'string' && templateData.previewDataUrl.startsWith('data:image/')
      ? templateData.previewDataUrl
      : '';

  for (const ti of slides) {
    const sid = ti?.id;
    if (!sid) continue;
    let u = pickRecordUrl(store[sid]);
    if (!u) {
      const altKey = Object.keys(store).find((k) => String(k) === String(sid));
      if (altKey) u = pickRecordUrl(store[altKey]);
    }
    if (!u) {
      try {
        u = await getImageFromIndexedDB(sid);
      } catch {
        u = '';
      }
    }
    if (!u && slides.length === 1 && previewDataUrl) {
      u = previewDataUrl;
    }
    if (u) map[sid] = u;
  }

  const storeKeys = Object.keys(store);
  if (storeKeys.length > 0 && slides.length > 0) {
    slides.forEach((ti, index) => {
      const sid = ti?.id;
      if (!sid || map[sid]) return;
      const rec = store[storeKeys[index]] ?? store[sid];
      const u = pickRecordUrl(rec);
      if (u) map[sid] = u;
    });
  }

  const resolved = {};
  await Promise.all(
    Object.entries(map).map(async ([sid, raw]) => {
      const drawable = await resolveDrawableImageUrl(raw, {
        imageId: sid,
        loadImageSpaceRecord: null,
      });
      if (drawable?.startsWith?.('data:image/')) {
        resolved[sid] = drawable;
      } else if (String(raw).startsWith('data:image/')) {
        resolved[sid] = raw;
      } else if (drawable) {
        // 非 data 但仍可签名加载：留给 Fabric resolveFabricJsonForRender
        resolved[sid] = drawable;
      } else {
        // 禁止清空——旧逻辑写 '' 会导致合成只有印花、模板底图丢失
        resolved[sid] = String(raw || '').trim();
      }
    }),
  );

  return resolved;
}

export function normalizeGoodsImageRefToId(imageRef) {
  if (imageRef == null) return '';
  if (typeof imageRef === 'object') {
    return String(imageRef.id || imageRef.imageId || imageRef.value || '').trim();
  }
  return String(imageRef).trim();
}

export function inlineUrlFromGoodsImageRef(imageRef) {
  if (!imageRef || typeof imageRef !== 'object') return '';
  const u = imageRef.url || imageRef.src || imageRef.dataUrl || imageRef.imageUrl || '';
  return u && String(u).trim() ? String(u).trim() : '';
}

/**
 * 合成用印花引用：尽量保留 { id, url }，避免压成纯 id 后 imageUrlMap 丢失时无法贴图。
 */
export function normalizeGoodsImageRefForCompose(imageRef) {
  if (imageRef == null || imageRef === '') return null;
  if (typeof imageRef === 'string') {
    const s = imageRef.trim();
    if (!s) return null;
    if (s.startsWith('data:image/') || s.startsWith('http') || s.startsWith('/') || s.startsWith('blob:')) {
      return { id: s, url: s };
    }
    return s;
  }
  const id = normalizeGoodsImageRefToId(imageRef);
  const url = inlineUrlFromGoodsImageRef(imageRef);
  if (id && url) return { id, url };
  if (url) return { id: id || url, url };
  if (id) return id;
  return null;
}

/** 轮播图 id 与 stampAreas 键不一致时，回退到模版内首个非空印花配置 */
export function resolveStampAreasForSlide(templateImageId, templateStampAreas) {
  const direct = templateStampAreas?.[templateImageId];
  if (Array.isArray(direct) && direct.length > 0) return direct;
  const altKey = Object.keys(templateStampAreas || {}).find((k) => String(k) === String(templateImageId));
  if (altKey) {
    const alt = templateStampAreas[altKey];
    if (Array.isArray(alt) && alt.length > 0) return alt;
  }
  return [];
}

/**
 * 仅当本组只填了 1 个印花位时，才把其余空位复用该图（单印花灌满前后）。
 * 已按正/背分配了多张不同印花时，禁止再把某一张复制进空位。
 */
export function expandImageGroupForStampAreas(imageGroup, stampAreas) {
  if (!imageGroup || typeof imageGroup !== 'object' || !Array.isArray(stampAreas)) {
    return imageGroup || {};
  }
  const filled = { ...imageGroup };
  const assigned = Object.entries(filled).filter(([, value]) => value != null && value !== '');
  // 多印花已分配（或全空）时不自动复制，避免「一张印花贴满前后」
  if (assigned.length !== 1) return filled;
  const primaryId = assigned[0][1];
  stampAreas.forEach((stamp) => {
    const name = stamp?.name;
    if (!name || filled[name]) return;
    filled[name] = primaryId;
  });
  return filled;
}

export function extractImageSpaceRecordRawUrl(image) {
  return resolveImageSpaceDisplayRawUrl(image);
}

/** 解析图片空间记录的可 fetch/展示 URL（无缓存时自动 sign-batch） */
export async function resolveUrlFromImageSpaceRecord(image) {
  const raw = extractImageSpaceRecordRawUrl(image);
  if (!raw) return '';
  if (raw.startsWith('data:image/') || raw.startsWith('blob:')) return raw;
  return resolveFetchableImageUrl(raw, image);
}

/** 提取图片空间记录的 canonical / data URL（同步，不含签名；fetch 请用 resolveUrlFromImageSpaceRecord） */
export function pickUrlFromImageSpaceRecord(image) {
  const raw = extractImageSpaceRecordRawUrl(image);
  if (!raw) return '';
  if (raw.startsWith('data:image/') || raw.startsWith('blob:')) return raw;
  return raw;
}
